import {
    createRef,
    ReactElement,
    RefObject,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

interface ScrollToOptions {
    instant?: boolean
}

interface UseScrollSnapCarouselOptions {
    initialIndex?: number
}

interface UseScrollSnapCarouselReturnValues {
    items: ReactElement[]
    containerRef: RefObject<HTMLDivElement>
    itemRefs: RefObject<HTMLDivElement>[]
    currentIndex: number
    prev: () => void
    next: () => void
    dragPrev: () => void
    dragNext: () => void
    cancelDrag: () => void
    scrollTo: (index: number, options?: ScrollToOptions) => boolean
}

const clampIndex = (index: number, length: number): number => Math.min(Math.max(index, 0), length - 1)

const scrollSettleDelay = 150

export const useScrollSnapCarousel = (
    items: ReactElement[],
    options: UseScrollSnapCarouselOptions = {},
): UseScrollSnapCarouselReturnValues => {
    const { initialIndex = 0 } = options
    const clampedInitialIndex = clampIndex(initialIndex, items.length)
    const [currentIndex, setCurrentIndex] = useState<number>(clampedInitialIndex)
    const dragTimout = useRef<ReturnType<typeof setTimeout> | null>(null)
    const settleTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
    const targetIndexRef = useRef(clampedInitialIndex)
    const hasInitializedScroll = useRef(false)

    const cancelSettle = () => {
        if (! settleTimeout.current) {
            return
        }

        clearTimeout(settleTimeout.current)
        settleTimeout.current = null
    }

    const containerRef = useMemo(
        () => createRef<HTMLDivElement>(),
        [],
    )

    const itemRefs = useMemo(
        () => items.map(() => createRef<HTMLDivElement>()),
        [items.length],
    )

    const scrollTo = (index: number, scrollOptions: ScrollToOptions = {}): boolean => {
        if (index < 0 || index > items.length - 1) {
            return false
        }

        const item = itemRefs[index]?.current
        targetIndexRef.current = index

        if (scrollOptions.instant) {
            cancelSettle()
            setCurrentIndex(index)
        }

        item?.scrollIntoView({
            behavior: scrollOptions.instant ? 'auto' : 'smooth',
            inline: 'center',
            block: 'nearest',
        })

        return true
    }

    const prev = () => {
        scrollTo(targetIndexRef.current - 1)
    }

    const next = () => {
        scrollTo(targetIndexRef.current + 1)
    }

    const dragPrev = () => {
        if (dragTimout.current) {
            return
        }

        dragTimout.current = setTimeout(() => {
            prev()
            dragTimout.current = null
        }, 500)
    }

    const dragNext = () => {
        if (dragTimout.current) {
            return
        }

        dragTimout.current = setTimeout(() => {
            next()
            dragTimout.current = null
        }, 500)
    }

    const cancelDrag = () => {
        if (! dragTimout.current) {
            return
        }

        clearTimeout(dragTimout.current)
        dragTimout.current = null
    }

    useLayoutEffect(() => {
        if (hasInitializedScroll.current) {
            return
        }

        hasInitializedScroll.current = scrollTo(clampedInitialIndex, { instant: true })
    }, [clampedInitialIndex, itemRefs, items.length])

    useEffect(() => {
        if (! containerRef.current) {
            return undefined
        }

        const observer = new IntersectionObserver(entries => {
            const visibleEntry = entries
                .filter(entry => entry.isIntersecting)
                .sort((entryA, entryB) => entryB.intersectionRatio - entryA.intersectionRatio)[0]

            if (! visibleEntry) {
                return
            }

            const index = itemRefs.findIndex(itemRef => itemRef.current === visibleEntry.target)

            cancelSettle()

            settleTimeout.current = setTimeout(() => {
                settleTimeout.current = null
                targetIndexRef.current = index
                setCurrentIndex(index)
            }, scrollSettleDelay)
        }, { root: containerRef.current, threshold: [0.5, 0.75, 1] })

        itemRefs.forEach(itemRef => {
            if (! itemRef.current) {
                return
            }

            observer.observe(itemRef.current)
        })

        return () => {
            observer.disconnect()
            cancelSettle()
        }
    }, [containerRef, itemRefs])

    return {
        items,
        containerRef,
        itemRefs,
        currentIndex,
        prev,
        next,
        dragPrev,
        dragNext,
        cancelDrag,
        scrollTo,
    }
}
